import {
  createContext,
  useContext,
  useMemo,
  FC,
  ReactNode,
  useCallback,
} from 'react';
import { useIsCofactr } from 'hooks/useIsCofactr';
import { useGetTeamsQuery } from 'store/slices/apiV1/properties';
import { Org } from 'types/org';
import { Team } from 'types/properties';
import { useIsFacilityUsingTeams } from 'views/warehouse/hooks/useIsFacilityUsingTeams';
import { FeatureFlags } from 'types';
import { useFeature } from '@growthbook/growthbook-react';
import { skipToken } from '@reduxjs/toolkit/dist/query/react';

type OrgAndTeamContextType = {
  orgsById: Record<string, { name: string; code: string }> | undefined;
  teams: Team[] | undefined;
  isLoadingTeams: boolean;
  isLoadingOrgs: boolean;
  tempDisplayOrgCode: boolean;
  facilityOrgId: string | undefined;
  isUsingTeams: boolean;
};

const OrgAndTeamContext = createContext<OrgAndTeamContextType | undefined>(
  undefined
);

export const OrgAndTeamProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const { user } = useIsCofactr();
  const { isUsingTeams, facility } = useIsFacilityUsingTeams();
  const tempDisplayOrgCode = useFeature(FeatureFlags.tempDisplayOrgCode);
  const { data: teams, isLoading: isLoadingTeams } = useGetTeamsQuery(
    isUsingTeams ? undefined : skipToken
  );

  const orgsById = useMemo(() => {
    if (!user?.orgs) return undefined;

    const orgMap: Record<string, { name: string; code: string }> = {};
    user.orgs.forEach((org: Org) => {
      orgMap[org.id] = {
        name: org.name,
        code: org.code,
      };
    });
    return orgMap;
  }, [user]);

  const value = useMemo(
    () => ({
      orgsById,
      teams,
      isLoadingTeams,
      isLoadingOrgs: !user,
      isUsingTeams,
      tempDisplayOrgCode: tempDisplayOrgCode.on,
      facilityOrgId: facility?.org,
    }),
    [
      orgsById,
      teams,
      isLoadingTeams,
      user,
      tempDisplayOrgCode,
      facility?.org,
      isUsingTeams,
    ]
  );

  return (
    <OrgAndTeamContext.Provider value={value}>
      {children}
    </OrgAndTeamContext.Provider>
  );
};

export const useOrgAndTeamContext = () => {
  const context = useContext(OrgAndTeamContext);
  if (context === undefined) {
    throw new Error(
      'useOrgAndTeamContext must be used within a OrgAndTeamProvider'
    );
  }

  const getOrgDisplay = useCallback(
    (orgId: string) => {
      if (context.facilityOrgId === orgId) {
        return undefined;
      }

      return context.tempDisplayOrgCode
        ? context.orgsById?.[orgId]?.code
        : context.orgsById?.[orgId]?.name;
    },
    [context.facilityOrgId, context.tempDisplayOrgCode, context.orgsById]
  );

  const getTeamDisplay = useCallback(
    (teamId: string | null) => {
      if (!context.isUsingTeams) return undefined;
      if (!teamId) return undefined;

      return context.teams?.find((t) => t.id === teamId)?.name;
    },
    [context.teams, context.isUsingTeams]
  );

  const getOrgOrTeamDisplay = useCallback(
    (orgId: string, teamId: string | null) => {
      const orgDisplay = getOrgDisplay(orgId);
      const teamDisplay = getTeamDisplay(teamId);
      return orgDisplay || teamDisplay;
    },
    [getOrgDisplay, getTeamDisplay]
  );

  return {
    ...context,
    getOrgDisplay,
    getTeamDisplay,
    getOrgOrTeamDisplay,
  };
};

export const OrgTeamDisplay = ({
  orgId,
  teamId,
}: {
  orgId: string;
  teamId: string | null;
}) => {
  const { getOrgOrTeamDisplay } = useOrgAndTeamContext();
  return <>{getOrgOrTeamDisplay(orgId, teamId)}</>;
};

export const OrgDisplay = ({ orgId }: { orgId: string }) => {
  const { getOrgDisplay } = useOrgAndTeamContext();
  return <>{getOrgDisplay(orgId)}</>;
};

export const TeamDisplay = ({ teamId }: { teamId: string | null }) => {
  const { getTeamDisplay } = useOrgAndTeamContext();
  return <>{getTeamDisplay(teamId)}</>;
};
